import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  BoxMultiple24Regular,
  Map24Regular,
  CameraDome24Regular,
  EyeTracking24Regular,
  CheckmarkCircle16Filled,
  Location24Regular,
} from '@fluentui/react-icons';
// `Button` direct from the design system rather than `@repo/ui`'s deprecated aliases: those
// exist only so pre-existing call sites re-skin for free, and their call-site count is meant to
// shrink. The secondary alias hard-codes `size="full"`, which would stretch "Go to twin" across
// the notice; the primary alias forwards no `aria-*`, and Go live now needs `aria-describedby`
// for the reason it is held (`launch-composite-refusals`, R7). Go live renders exactly what that
// alias rendered — `variant="primary" size="full"`.
import { Banner, Button, Card, Icon, Note } from '@repo/design-system';
import { nothingMeasuredNote, partlyMeasuredLaunchNote, visuallyHidden } from '@repo/ui';
import { brand, icon, ink, semantic, space, typeRole } from '@repo/design-tokens';
import { NOT_IN_CATALOGUE_LABEL, type OnboardingMode } from '@repo/front-end-utils';
import type { RegisterReadState } from '@repo/front-end-state';
import type { BiImportFailure } from '@repo/front-end-services';
import LaunchDetail from './LaunchDetail';
import LaunchBiNotice from './LaunchBiNotice';
import type { BiPushState } from './useLaunchBiPush';
import type { LaunchLockView } from './useLaunchLock';
import type { FloorRemovalRecord } from '@repo/frontend-types';
import { LaunchRemovedFloors } from './LaunchRemovedFloors';
import { LaunchRenumbered, type RenumberedRow } from './LaunchRenumbered';
import type { PutBackOutcome } from './putBackFloor';
import {
  BI_GAP_HEADINGS,
  entityItem,
  type BiGapGroups,
  type BiInputs,
  type LaunchItem,
  type LaunchPlacement,
} from './launchSummaryDetail';
import type { LaunchBiCheckState } from './useLaunchBiCheck';
import { launchDraftLine, type DraftOrigin } from './versionsText';
import { openingHoursReady, type LaunchHoursState } from './useLaunchHours';
import { announcementFor, announcementKey, noteFor, summaryFor } from './launchHoursText';
import { LaunchFlagNote, LaunchSentence, strongestFlag, type LaunchFlag } from './launchFlags';

export interface LaunchSummary {
  /**
   * Where the twin is. `located` is the same predicate Go Live's origin chain gates on, so this
   * block agrees with what pressing "Go live" will actually do.
   */
  twinDetails: {
    located: boolean;
    /** Sitting on the legacy `51.4779 / 0` placeholder — activates, but almost certainly wrong. */
    placeholder: boolean;
    address: string | null;
    timezone: string | null;
    /**
     * `failed` when the page's twin read found nothing and the Twin details step has not reported
     * (`twin-details-page-landing`, R3): unlocated is then unknown, not "No position set".
     */
    read: 'pending' | 'ready' | 'failed';
  };
  /**
   * `read` is what tells "this twin has no assets" apart from "the register could not be read".
   * All three counts are zero in both cases, and this is the screen an operator reads immediately
   * before Go Live (`wizard-register-on-store`).
   */
  assets: {
    read: RegisterReadState;
    total: number;
    matched: number;
    /**
     * Rows a person decided have NO catalogue product (`uncatalogued-terminal-state`, ledger row
     * 119) — neither matched nor unknown. Its clause is omitted when zero, so every twin without
     * one reads exactly as it did.
     *
     * The ROWS, not a number (`launch-summary-detail`, R6): the count printed is this list's length,
     * so the sentence and the list under it cannot disagree.
     */
    uncatalogued: LaunchItem[];
    /** Rows still awaiting a catalogue match. As above, the count is the list's length. */
    unknown: LaunchItem[];
  };
  /**
   * `placement` is the register rows the plan does not hold — the register table's *Not on plan*
   * set — or why that cannot be said yet. It used to be a bare `unplaced` number, a subtraction
   * with no read state, so a plan still loading read as "N not yet placed".
   */
  plan: {
    drawn: boolean;
    placed: number;
    placement: LaunchPlacement;
  };
  cameras: {
    linked: number;
    unlinked: number;
    /** The step list's Camera Feeds answer (`feedsCompletion.ts`), passed in so both ticks agree. */
    complete: boolean;
  };
  /**
   * `read` for the same reason `assets.read` carries one: both numbers are now derived from the
   * twin's live configuration, so on a failed read they would otherwise say "0 with an observer,
   * 160 without one yet" — an affirmative claim that the configuration is empty, sitting directly
   * above a coverage block honestly saying it could not be read.
   */
  observers: {
    read: 'loading' | 'ready' | 'error' | 'plan-error';
    created: number;
    /** Entities with no observer of their own. The count printed is this list's length. */
    withoutObserver: LaunchItem[];
  };
  /**
   * What nothing measures — an observer of its own, or a complex measure that totals it.
   *
   * `read` is what tells "everything on this plan is tracked" apart from "we could not find out",
   * and the distinction is the whole reason this is not just a number. A failed configuration read
   * leaves the plan holding none of the twin's live measures, so a count taken then would report
   * entities as untracked on the strength of a read that did not happen — the same false claim the
   * `assets` block above exists to avoid, about a different thing.
   *
   * `names` is every uncovered entity. It is rendered in a closed-by-default list, so it is not
   * capped (`launch-summary-detail`, R5): a slice would present part of the list as the whole.
   */
  coverage: {
    /**
     * `no-plan` is its own answer, not a kind of loading. A twin with no saved plan — and one whose
     * plan read failed, which swallows its error and yields null — would otherwise sit on "still
     * reading" for ever, which is the same spin-with-no-answer trap `liveReadState` exists to avoid
     * one level up.
     */
    read: 'loading' | 'ready' | 'error' | 'no-plan';
    total: number;
    observed: number;
    totalled: number;
    uncovered: number;
    /** The refid keys the list: display names are not unique on a plan. */
    names: { refid: string; name: string }[];
    /**
     * Complex measures that are not finished — see `entityCoverage`'s `CoverageBlock`.
     *
     * ⚠ **Informative, never a gate.** These totals go live adding up only the sources that are
     * measured (`onboarding-polish-partial-composites`).
     */
    unfinished: number;
    unfinishedNames: { refid: string; name: string }[];
    /**
     * Not finished and nothing measured — held back from the live system. Informative, never a gate.
     * Optional: absent reads as none.
     */
    nothingMeasuredNames?: { refid: string; name: string }[];
    /**
     * Every composite measure the twin would refuse, with the Composite measures step's own
     * sentences — see `entityCoverage`'s `CoverageBlock`.
     *
     * ⛔ **The one gate on this page's coverage block.** Go Live is disabled while this is non-empty
     * — in every read state, because it is read from the plan Go Live files
     * (`composites-page-launch-refusals`, R6; ruled by maria@twinlabs.ai, 2026-10-04).
     */
    refused: { refid: string; name: string; reasons: string[] }[];
    /** `'failed'` when the refusal check itself threw: say so, and do not hold (R6a). */
    refusalCheck: 'ok' | 'failed';
    /** Measures that add up nothing. Never a gate, and not also in `unfinishedNames`. */
    empty: { refid: string; name: string }[];
    /**
     * Generated totals worth a person's look — see `entityCoverage`'s `CoverageBlock`. Never a gate,
     * and not also in any group above. Optional: absent reads as none.
     */
    review?: { refid: string; name: string }[];
  };
  /**
   * What BI will be missing for the placed items (`launch-bi-gap-preview`, R2): the *BI hierarchies*
   * block. ⚠ **Informative, never a gate** — nothing here holds Go live (R2.4). Optional: absent, as
   * for `hours`, the block is not shown, so the step's own tests render unchanged.
   */
  biGaps?: LaunchBiGaps;
}

/** The *BI hierarchies* block's inputs (`launch-bi-gap-preview`, R2.3). */
export interface LaunchBiGaps {
  /**
   * Whether the asset register and the plan have been read: until both have, who is placed and what
   * their references are is not known. `register-error` and `plan-error` cannot be checked at all.
   */
  inputs: BiInputs;
  /** Placed items: every live plan item, each of which becomes an Asset entity at Go Live. */
  placed: number;
  groups: BiGapGroups;
  check: LaunchBiCheckState;
}

interface Props {
  className?: string;
  summary: LaunchSummary;
  onGoLive: () => void;
  /**
   * The Go live button itself, so the page can give it focus back after a person cancels Launch's
   * confirmation (`launch-single-confirmation`, criterion 10). The button is disabled while Go
   * Live runs, which drops focus to the page, so the dialog has nothing of its own to return to.
   */
  goLiveRef?: React.Ref<HTMLButtonElement>;
  /** True while activation is running — disables the button and shows progress. */
  activating?: boolean;
  /** Activation error to surface below the button, if any. */
  error?: string | null;
  /**
   * Partial-success message: the twin is live, but not everything the user drew
   * made it. Shown with a "Go to twin" action so they aren't stranded.
   */
  notice?: string | null;
  /**
   * Continue into the live twin from the partial-success notice. Required, not
   * optional: a notice with no way out is precisely the stranded user it exists
   * to prevent, so the pair travels together.
   */
  onContinue: () => void;
  /**
   * Whether the wizard is creating this twin or updating an established one. Copy only — the
   * activation behind the button is identical either way; it is Go Live's delta guard, not this
   * value, that decides what actually happens (spec `twin-onboarding-onboarding-states`, FR5).
   */
  mode?: OnboardingMode;
  /**
   * Launch's BI step (`twin-launch-history-bi-push`). When it failed, the twin is live and the
   * banner offers a Retry of the BI part only, or continuing without it; Go live is held meanwhile,
   * because the launch has happened and pressing it again would re-file. Optional so the stage
   * renders without it; absent reads as idle.
   */
  bi?: BiPushState;
  onRetryBi?: () => void;
  onContinueWithoutBi?: () => void;
  /**
   * The person's Retry (once it succeeded) or Continue is being saved to the launch history
   * (`twin-launch-history-bi-outcome`, R3.8). The banner stays up meanwhile — a successful Retry
   * leaves the BI step `done`, which on its own would remove it — showing `biSavingFailure`, the
   * failure it was showing, with the pressed button busy.
   */
  biOutcomeSaving?: 'retry' | 'continue' | null;
  biSavingFailure?: BiImportFailure | null;
  /**
   * The 60-second Go live lock after a launch (spec twin-launch-history-record). Only the button
   * locks; nothing else on the step does. Absent when the step is rendered without a twin to ask
   * about (its own tests).
   */
  lock?: LaunchLockView;
  /** Read the lock again — the *Check again* button on a failed read. */
  onRecheckLock?: () => void;
  /**
   * Whether the twin's opening hours let it launch (spec twin-details-page-launch-gate, R7): held
   * while checking, when they could not be read, and when they do not. Absent, as for `lock`, when
   * the step is rendered without a twin to ask about (its own tests): nothing is held and the
   * *Opening hours* block is not shown. The page always passes it.
   */
  hours?: LaunchHoursState;
  /** A *Check again* read of the hours is in flight. */
  hoursRechecking?: boolean;
  /** Read the hours again — the *Check again* button on a failed read. */
  onRecheckHours?: () => void;
  /** A *Check again* of the BI reference check is in flight. */
  biRechecking?: boolean;
  /** Check the references against BI again — the *BI hierarchies* block's *Check again*. */
  onRecheckBi?: () => void;
  /**
   * Floors deleted from the live twin since its last launch (`floor-plan-page-floors`, R18): listed
   * under *Removed at Launch* with *Put back*. Absent or empty shows nothing.
   */
  removedFloors?: readonly FloorRemovalRecord[];
  /** Live spaces re-coded since the last launch (`floor-plan-page-launch-bid-changes`, R16). */
  renumbered?: readonly RenumberedRow[];
  /** Put one back and save the plan; resolves with what happened, for the toast. */
  onPutBackFloor?: (record: FloorRemovalRecord) => Promise<PutBackOutcome>;
  /** Save the plan again, after a put-back's save failed. */
  onRetryPlanSave?: () => Promise<{ saved: boolean; reason?: string | null }>;
  /**
   * Where the draft comes from, as the chip names it (`twin-details-page-load-launch`, R6) —
   * repeated in the intro as *from v3*; nothing is said for a new or migrating twin, or with no
   * history read.
   */
  draftOrigin?: DraftOrigin;
}

/** `65` → `1:05`. The lock is at most a minute, but nothing here assumes it. */
const clock = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/** Whole seconds until `endsAt` and since `launchedAt`, both in `performance.now()` time. */
const lockSeconds = (endsAt: number, launchedAt: number | null) => {
  const now = performance.now();
  return {
    left: Math.max(0, Math.ceil((endsAt - now) / 1000)),
    since: launchedAt === null ? 0 : Math.max(0, Math.floor((now - launchedAt) / 1000)),
  };
};

/**
 * The countdown line. It ticks on its own, once a second, so only this line re-renders while the
 * lock runs — never the step, and never the wizard page above it.
 */
const LockCountdown: React.FC<{
  endsAt: number;
  launchedAt: number | null;
  label: string;
  inProgress: boolean;
}> = ({ endsAt, launchedAt, label, inProgress }) => {
  const [seconds, setSeconds] = useState(() => lockSeconds(endsAt, launchedAt));
  useEffect(() => {
    setSeconds(lockSeconds(endsAt, launchedAt));
    const id = setInterval(() => setSeconds(lockSeconds(endsAt, launchedAt)), 1000);
    return () => clearInterval(id);
  }, [endsAt, launchedAt]);
  const ago = `${seconds.since} second${seconds.since === 1 ? '' : 's'} ago`;
  return (
    <p id="launch-go-live-locked" className="go-live-held">
      {inProgress
        ? `A launch of this twin started ${ago} and has not finished. ${label} is held until it does.`
        : `Launched ${ago} — ${label} is available again in ${clock(seconds.left)}.`}
    </p>
  );
};

/** Small tick, in the step list's success colour, shown next to a step that's fully complete. */
const StepTick: React.FC<{ done: boolean }> = ({ done }) =>
  done ? (
    <span className="tick" title="Complete">
      <CheckmarkCircle16Filled />
    </span>
  ) : null;

/**
 * The *Measurement coverage* heading glyph — four cells, three filled and one not.
 *
 * A LOCAL inline SVG, and the reason is the same one `AttentionGlyph` in `ObserverInventoryPanel`
 * gives: the design system ships no icon primitive and no icon set (design-system epic §6B, raised
 * 2026-08-26), and `@fluentui/react-icons` is ratchet-only, so a new import from it is barred even
 * though this file already holds seven. Rob accepted this interim in-session on 2026-09-11 for the
 * Observers stage's attention glyph, as he did for the camera wall's six and the setup-app's two;
 * this is the fifth feature to reach the gap and the eleventh such SVG. It retires onto the icon
 * primitive when that is ruled.
 *
 * One colour, inherited: everything is `currentColor`, so it takes `.block-head`'s colour and adds
 * no literal of its own. Sized from the JS `icon` token rather than a `var()` — `width` and
 * `height` are presentation attributes and reject a custom property, falling back to 300x150 (§6B
 * records the failure). `icon.xl` is 24px, which is the size of the five Fluent glyphs beside it.
 *
 * `aria-hidden`: the `<h4>` beside it carries the name.
 */
const CoverageGlyph: React.FC = () => (
  <svg
    className="coverage-glyph"
    viewBox="0 0 24 24"
    width={icon.xl}
    height={icon.xl}
    aria-hidden="true"
    focusable="false"
  >
    <g fill="currentColor">
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </g>
    <rect
      x="13.75"
      y="13.75"
      width="6.5"
      height="6.5"
      rx="1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

/**
 * Launch Twin — the pre-flight summary of everything set up across the
 * onboarding sections, and the "Go live" action.
 */
/** `1 asset` / `7 assets` — the count and noun a Launch list's accessible name carries. */
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/** The Equipment/Assets list's accessible name names both groups it holds, and only those. */
const describeAssets = (uncatalogued: number, unknown: number): string =>
  [
    uncatalogued > 0 ? `${plural(uncatalogued, 'asset')} not in the catalogue` : null,
    unknown > 0 ? `${plural(unknown, 'unknown asset')}` : null,
  ]
    .filter(Boolean)
    .join(' and ');

/** The composite-refusals list's accessible name — the groups it holds, and only those. */
const describeCompositeRefusals = (refused: number, empty: number): string =>
  [
    refused > 0 ? `${plural(refused, 'composite measure')} that would be refused` : null,
    empty > 0 ? plural(empty, 'empty composite measure') : null,
  ]
    .filter(Boolean)
    .join(' and ');

/**
 * The *Opening hours* block's flag: hours that stop go-live, or hours that start later. Only on a
 * read that answered: checking and a failed read are said plainly, with Go live's own line below.
 */
const hoursFlag = (hours: LaunchHoursState): LaunchFlag | null => {
  if (hours.read !== 'ready') return null;
  switch (hours.launchHours.reason) {
    case 'no_open_day':
    case 'no_timezone':
    case 'no_standard':
      return 'hoursHeld';
    case 'upcoming':
      return 'hoursUpcoming';
    default:
      return null;
  }
};

/** "The register could not be read" and its plan twin: the check has nothing to run on. */
const BI_INPUT_ERRORS: Record<'register-error' | 'plan-error', string> = {
  'register-error': 'The asset register could not be read, so this cannot be checked.',
  'plan-error': 'The plan could not be read, so this cannot be checked.',
};

/**
 * The *BI hierarchies* block (`launch-bi-gap-preview`, R2.3): which placed items will reach BI with
 * no manufacturer or no equipment type, before the rocket. One sentence per state, in an
 * always-mounted live region, and a tick ONLY when BI answered and nothing is missing — a check that
 * failed is never a pass.
 */
const BiHierarchiesBlock: React.FC<{
  biGaps: LaunchBiGaps;
  rechecking: boolean;
  onRecheck?: () => void;
}> = ({ biGaps, rechecking, onRecheck }) => {
  const { inputs, placed, groups, check } = biGaps;
  const { noReference, notInManufacturer, noEquipmentType } = groups;
  const answered = check.status === 'ready';
  // No references at all: nothing to ask BI, so the no-reference group is the whole answer.
  const nothingToAsk = groups.references.length === 0;
  const gaps = noReference.length + notInManufacturer.length + noEquipmentType.length;
  const ready = inputs === 'ready' && placed > 0;
  const failed = ready && !nothingToAsk && check.status === 'failed';
  const settled = ready && (nothingToAsk || answered);
  const clean = settled && gaps === 0;

  const gapsSentence = (
    <>
      <strong>{gaps}</strong> of <strong>{placed}</strong> placed item{placed === 1 ? '' : 's'} will
      reach BI with gaps.
    </>
  );

  let sentence: React.ReactNode;
  if (inputs === 'reading') sentence = 'Still reading the asset register and plan…';
  else if (inputs !== 'ready') sentence = BI_INPUT_ERRORS[inputs];
  else if (placed === 0)
    sentence = "No equipment placed, so nothing goes to BI's manufacturer hierarchy.";
  else if (failed) {
    sentence = (
      <>
        Couldn&rsquo;t check the catalogue references against BI.
        {noReference.length > 0 ? (
          <>
            {' '}
            <strong>{noReference.length}</strong> placed item
            {noReference.length === 1 ? ' has' : 's have'} no catalogue reference.
          </>
        ) : null}
      </>
    );
  } else if (!settled) {
    sentence = `Checking ${plural(groups.references.length, 'reference')} against BI…`;
  } else if (clean) {
    sentence = (
      <>
        All <strong>{placed}</strong> placed item{placed === 1 ? '' : 's'} will reach BI&rsquo;s
        manufacturer and equipment-type hierarchies.
      </>
    );
  } else sentence = gapsSentence;

  // Before BI answers (checking, or a failed check), only what is already known is listed.
  const listed = settled
    ? [
        { heading: 'No catalogue reference', items: noReference },
        { heading: BI_GAP_HEADINGS.notInManufacturer, items: notInManufacturer },
        { heading: BI_GAP_HEADINGS.noEquipmentType, items: noEquipmentType },
      ]
    : [{ heading: 'No catalogue reference', items: ready ? noReference : [] }];
  const listedCount = listed.reduce((n, group) => n + group.items.length, 0);

  return (
    <div className="launch-block">
      <div className="block-head">
        <Icon name="sitemap" size="xl" />
        <h4>BI hierarchies</h4>
        <StepTick done={clean} />
      </div>
      <p className="coverage-note coverage-subheading">
        Shows what BI will be missing. It never stops you going live.
      </p>
      {/* Always mounted, with the state inside it: a region that appears when it has something to
          say is announced to nobody (see the coverage block above). A `<div>`, since a flagged
          sentence is a `Note`. Gaps are flagged only once BI has answered; a failed check is plain. */}
      <div aria-live="polite" aria-atomic="true">
        <LaunchSentence
          flag={
            settled && gaps > 0
              ? strongestFlag([noReference.length > 0 && 'biNoReference', 'biGapsOnly'])
              : null
          }
        >
          {sentence}
        </LaunchSentence>
      </div>
      {failed && onRecheck && (
        <div>
          {/* `loading`, not `disabled`: the button keeps focus and says it is busy. */}
          <Button
            variant="text"
            size="xs"
            onClick={onRecheck}
            loading={rechecking}
            aria-label="Check again: the catalogue references against BI"
          >
            {rechecking ? 'Checking…' : 'Check again'}
          </Button>
        </div>
      )}
      <LaunchDetail
        id="launch-detail-bi-gaps"
        label={`${plural(listedCount, 'item')} with BI gaps`}
        groups={listed}
      />
    </div>
  );
};

const OnboardingLaunch: React.FC<Props> = ({
  className,
  summary,
  onGoLive,
  goLiveRef,
  activating = false,
  error,
  notice,
  onContinue,
  bi,
  onRetryBi,
  onContinueWithoutBi,
  biOutcomeSaving = null,
  biSavingFailure = null,
  mode = 'CREATE',
  lock,
  onRecheckLock,
  hours,
  hoursRechecking = false,
  onRecheckHours,
  biRechecking = false,
  onRecheckBi,
  removedFloors,
  renumbered,
  onPutBackFloor,
  onRetryPlanSave,
  draftOrigin,
}) => {
  // Where focus lands when the last removed floor is put back and its list unmounts.
  const blocksRef = useRef<HTMLDivElement>(null);
  const { twinDetails, assets, plan, cameras, observers, coverage } = summary;
  const updating = mode === 'UPDATE';
  const draftLine = launchDraftLine(draftOrigin);
  // Copy only; the action is identical in both modes.
  const goLiveLabel = updating ? 'Update twin' : 'Go live';

  /*
   * The launch lock. Any state but `unlocked` holds the button — including `unknown`: "we could not
   * find out when this twin was last launched" is not permission to launch it again.
   */
  const lockHeld = lock !== undefined && lock.state !== 'unlocked';
  /*
   * The opening hours (spec twin-details-page-launch-gate, R7). Held while they are being read, when
   * the read failed ("could not check" is not permission, as for the lock) and when they do not let
   * the twin launch. The server's claim refuses on the same rule whatever this says.
   */
  const hoursHeld = hours !== undefined && !openingHoursReady(hours);
  const hoursLine = hours !== undefined ? noteFor(hours, goLiveLabel) : null;
  const [hoursAnnouncement, setHoursAnnouncement] = useState('');
  const previousHoursKeyRef = useRef<string | undefined>(undefined);
  const hoursKey = hours !== undefined ? announcementKey(hours, hoursRechecking) : undefined;
  useEffect(() => {
    if (hours === undefined) return;
    const said = announcementFor(previousHoursKeyRef.current, hours, goLiveLabel, hoursRechecking);
    previousHoursKeyRef.current = hoursKey;
    if (said !== null) setHoursAnnouncement(said);
    // Keyed on the reduced state alone: a re-read answering the same thing says nothing, while a
    // Check again clears the region so a second failure is announced again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoursKey, goLiveLabel]);
  /*
   * Announced on TRANSITIONS only, never per second: a polite region whose text changed every
   * second would read the countdown aloud for a minute. The seconds are captured at the moment the
   * lock starts, through a ref, so the effect keys on the state alone.
   */
  /*
   * Announced on TRANSITIONS only, never per second: a polite region whose text changed every
   * second would read the countdown aloud for a minute. The previous state starts UNSET, so a step
   * that opens already locked, or on a failed read, announces it — the disabled button cannot be
   * focused, so its description is never heard by tabbing. "Checking" clears the region, so a
   * second failure after Check again is announced again rather than set to the same text.
   */
  const [lockAnnouncement, setLockAnnouncement] = useState('');
  const previousLockStateRef = useRef<string | undefined>(undefined);
  /** The last settled state, so Check again stays mounted while it re-checks (focus is kept). */
  const lastSettledRef = useRef<string | undefined>(undefined);
  if (lock && lock.state !== 'checking') lastSettledRef.current = lock.state;
  const recheckingAfterUnknown = lock?.state === 'checking' && lastSettledRef.current === 'unknown';
  useEffect(() => {
    const previous = previousLockStateRef.current;
    previousLockStateRef.current = lock?.state;
    if (lock?.state === previous) return;
    if (lock?.state === 'locked' && lock.endsAt !== null) {
      const { left } = lockSeconds(lock.endsAt, lock.launchedAt);
      setLockAnnouncement(
        lock.inProgress
          ? `${goLiveLabel} is held: a launch of this twin is in progress.`
          : `${goLiveLabel} is locked for ${left} seconds after a launch.`,
      );
    } else if (lock?.state === 'unlocked' && previous === 'locked') {
      setLockAnnouncement(`${goLiveLabel} is available again.`);
    } else if (lock?.state === 'unknown') {
      setLockAnnouncement("Couldn't check when this twin was last launched.");
    } else if (lock?.state === 'checking') {
      setLockAnnouncement('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- announces lock transitions only; `inProgress` is read when one happens
  }, [lock?.state, lock?.endsAt, lock?.launchedAt, goLiveLabel]);

  // Per-step completion (for the "what's done" ticks).
  const registerRead = assets.read === 'ready';
  const equipmentDone = registerRead && assets.total > 0 && assets.unknown.length === 0;
  const planDone = plan.drawn;
  const observersDone = observers.read === 'ready' && observers.created > 0;
  // Ready AND nothing left uncovered. A tick on an unread count would claim the very thing the
  // three-state rendering below exists to avoid claiming.
  const coverageDone = coverage.read === 'ready' && coverage.uncovered === 0;

  // A total the twin would refuse: Go Live is held (`composites-page-launch-refusals`, R6). NOT
  // gated on the read — the plan this is read from is the plan Go Live would file right now.
  const refusedCount = coverage.refused.length;
  const held = refusedCount > 0;
  const checkFailed = coverage.refusalCheck === 'failed';
  /*
   * The hold LIFTING is a state change too, and it can happen with Launch open: before the twin's
   * own configuration is placed, a saved copy and a drawn measure on one target read as a
   * duplicate, and the placement then supersedes the drawn one. Without a sentence for it the
   * clause simply unmounts — a region emptying is announced by nothing — and Go live silently
   * comes back. Adjusted DURING render when `held` flips (React's "adjust state when a prop
   * changes" pattern), so the sentence and the enabled button arrive in the same commit.
   */
  const [wasHeld, setWasHeld] = useState(held);
  const [released, setReleased] = useState(false);
  if (held !== wasHeld) {
    setWasHeld(held);
    setReleased(!held);
  }
  /*
   * ⚠ **Never "released" on a check that did not run.** A check that throws reports no refusals, so
   * `held` flips to false exactly as it does when the last refusal is fixed — and the released
   * sentence beside R6a's would claim nothing would be refused on the strength of a check that
   * failed (spec R6a; review of this change, 2026-10-05).
   */
  const showReleased = released && !checkFailed;
  // The empty group waits for the twin's own measures to be on the plan, like the unfinished one.
  const emptyShown = coverage.read === 'ready' ? coverage.empty : [];
  const reviewShown = coverage.review ?? [];
  const nothingMeasuredNames = coverage.nothingMeasuredNames ?? [];

  // Flag when the user skipped telling us about their equipment entirely.
  const equipmentSkipped = registerRead && assets.total === 0;

  // The BI step failed (or its retry is running) after the twin went live: the banner shows, and Go
  // live is held — the launch has happened, and pressing it again would re-file.
  const biFailure =
    bi?.failure && (bi.status === 'failed' || bi.status === 'running')
      ? bi.failure
      : biOutcomeSaving
        ? (biSavingFailure ?? bi?.failure ?? null)
        : null;
  const biHolding = biFailure !== null;

  return (
    /*
     * One glass panel for the whole column (`onboarding-persistent-scene-wash-panels-glass`, R5.1):
     * the design system's Card on-scene, so the step draws one blur over the wash. Everything inside
     * it is an inlay or a control on glass.
     */
    <Card surface="on-scene" size="lg" className={className}>
      <p className="intro">
        {updating ? (
          <>
            You&rsquo;re ready to update this twin. Here&rsquo;s what&rsquo;s set up — you can
            update it now and refine any step later.
          </>
        ) : (
          <>
            You&rsquo;re ready to launch. Here&rsquo;s what you&rsquo;ve set up — you can go live
            now and refine any step later.
          </>
        )}
        {draftLine && <> {draftLine}</>}
      </p>

      {equipmentSkipped && (
        <LaunchFlagNote flag="equipmentSkipped" role="alert">
          <strong>You haven&rsquo;t added your equipment.</strong> Without an accurate picture of
          your equipment, you&rsquo;ll miss out on key insights. Head back to{' '}
          <strong>Equipment/Assets</strong> to import them.
        </LaunchFlagNote>
      )}

      <div className="blocks" ref={blocksRef} tabIndex={-1}>
        {removedFloors && removedFloors.length > 0 && onPutBackFloor && onRetryPlanSave && (
          <LaunchRemovedFloors
            records={removedFloors}
            onPutBack={onPutBackFloor}
            onRetrySave={onRetryPlanSave}
            focusAfterLast={() => blocksRef.current?.focus()}
            launching={activating}
          />
        )}
        {renumbered && renumbered.length > 0 && <LaunchRenumbered rows={renumbered} />}
        <div className="launch-block">
          <div className="block-head">
            <Location24Regular />
            <h4>Twin details</h4>
            <StepTick done={twinDetails.located} />
          </div>
          {twinDetails.located ? (
            <>
              <p>
                {twinDetails.address ? (
                  <>
                    <strong>{twinDetails.address}</strong> —{' '}
                  </>
                ) : null}
                position set
                {twinDetails.timezone ? (
                  <>
                    , timezone <strong>{twinDetails.timezone}</strong>
                  </>
                ) : null}
                .
              </p>
              {/* Its own sentence, after the paragraph, so it can carry its flag; plain off a ready read. */}
              {twinDetails.placeholder ? (
                <LaunchSentence flag={twinDetails.read === 'ready' ? 'placeholderPosition' : null}>
                  <strong>That position is the old placeholder default</strong> — a real point, so
                  it will not block Go live, but almost certainly not where this twin is. Set the
                  real one in the Twin details step.
                </LaunchSentence>
              ) : null}
            </>
          ) : twinDetails.read === 'pending' ? (
            <p>Checking the twin&rsquo;s position…</p>
          ) : twinDetails.read === 'failed' ? (
            <p>
              <strong>Could not read the twin&rsquo;s details.</strong> Open the{' '}
              <strong>Twin details</strong> step to check its position.
            </p>
          ) : (
            <LaunchFlagNote flag="noPosition">
              <strong>No position set.</strong> Go live needs one — it is refused rather than
              placing the twin at 0,0. Set it in the <strong>Twin details</strong> step. A twin that
              is already live keeps the position it is already rendered at.
            </LaunchFlagNote>
          )}
        </div>

        {hours !== undefined && (
          <div className="launch-block">
            <div className="block-head">
              <Icon name="clock" size="xl" />
              <h4>Opening hours</h4>
              <StepTick done={openingHoursReady(hours)} />
            </div>
            <LaunchSentence flag={hoursFlag(hours)}>
              {summaryFor(hours, goLiveLabel).map((part, i) =>
                part.strong ? (
                  <strong key={i}>{part.text}</strong>
                ) : (
                  <span key={i}>{part.text}</span>
                ),
              )}
            </LaunchSentence>
          </div>
        )}

        <div className="launch-block">
          <div className="block-head">
            <BoxMultiple24Regular />
            <h4>Equipment/Assets</h4>
            <StepTick done={equipmentDone} />
          </div>
          <LaunchSentence
            flag={
              assets.read === 'ready'
                ? strongestFlag([
                    assets.unknown.length > 0 && 'assetsUnknown',
                    assets.uncatalogued.length > 0 && 'assetsUncatalogued',
                  ])
                : null
            }
          >
            {/*
              Three states, not two. Never "0 assets uploaded" for a register nobody has read back —
              on a twin whose import succeeded that sentence reads as a lost import — but "could not
              be read" is equally wrong while the read is still in flight, and would send an
              operator chasing a failure that has not happened. Only `error` gets the failure
              sentence.
            */}
            {assets.read === 'ready' ? (
              <>
                <strong>{assets.total}</strong> asset{assets.total === 1 ? '' : 's'} uploaded —{' '}
                <strong>{assets.matched}</strong> matched,{' '}
                {assets.uncatalogued.length > 0 && (
                  <>
                    <strong>{assets.uncatalogued.length}</strong>{' '}
                    {NOT_IN_CATALOGUE_LABEL.toLowerCase()},{' '}
                  </>
                )}
                <strong>{assets.unknown.length}</strong> unknown.
              </>
            ) : assets.read === 'error' ? (
              <>The asset register could not be read, so this cannot be summarised.</>
            ) : (
              <>Still reading the asset register…</>
            )}
          </LaunchSentence>
          {/* Only on a READ register: a list drawn from an unread one would be empty and look whole. */}
          {assets.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-assets"
              label={describeAssets(assets.uncatalogued.length, assets.unknown.length)}
              groups={[
                { heading: NOT_IN_CATALOGUE_LABEL, items: assets.uncatalogued },
                { heading: 'Unknown', items: assets.unknown },
              ]}
            />
          )}
        </div>

        <div className="launch-block">
          <div className="block-head">
            <Map24Regular />
            <h4>Floor Plan</h4>
            <StepTick done={planDone} />
          </div>
          {/*
            The unplaced set is the register table's *Not on plan* set, and it needs BOTH reads.
            A plan not yet read, or failed, says so and nothing else. A register not yet read, or
            failed, is not a floor plan that could not be read: the block keeps what the plan
            alone answers ("Plan drawn — N placed") and says the unplaced part waits on the
            register. Live, because it settles after first paint: a `<div>`, since a flagged
            sentence is a `Note`.
          */}
          <div aria-live="polite" aria-atomic="true">
            <LaunchSentence
              flag={
                plan.placement.kind === 'ready' && plan.placement.unplaced.length > 0
                  ? 'unplaced'
                  : null
              }
            >
              {plan.placement.kind === 'plan-loading' ? (
                <>Still reading the floor plan…</>
              ) : plan.placement.kind === 'plan-error' ? (
                <>The floor plan could not be read, so placement cannot be summarised.</>
              ) : (
                <>
                  {plan.drawn ? 'Plan drawn' : 'No plan drawn yet'} — <strong>{plan.placed}</strong>{' '}
                  piece{plan.placed === 1 ? '' : 's'} of equipment placed
                  {plan.placement.kind === 'ready' ? (
                    <>
                      , <strong>{plan.placement.unplaced.length}</strong> not yet placed.
                    </>
                  ) : plan.placement.register === 'loading' ? (
                    <>. Still reading the asset register to see what is not yet placed.</>
                  ) : plan.placement.register === 'error' ? (
                    <>
                      . The asset register could not be read, so what is not yet placed is unknown.
                    </>
                  ) : (
                    <>.</>
                  )}
                </>
              )}
            </LaunchSentence>
          </div>
          {plan.placement.kind === 'ready' && (
            <LaunchDetail
              id="launch-detail-unplaced"
              label={`${plural(plan.placement.unplaced.length, 'asset')} not yet placed`}
              groups={[{ items: plan.placement.unplaced }]}
            />
          )}
        </div>

        <div className="launch-block">
          <div className="block-head">
            <CameraDome24Regular />
            <h4>Camera Feeds</h4>
            <StepTick done={cameras.complete} />
          </div>
          {/* No read state to wait on: the counts are the flag's whole input, as they are today's sentence's. */}
          <LaunchSentence flag={cameras.unlinked > 0 ? 'camerasUnlinked' : null}>
            <strong>{cameras.linked}</strong> camera{cameras.linked === 1 ? '' : 's'} linked to a
            feed, <strong>{cameras.unlinked}</strong> not linked.
          </LaunchSentence>
        </div>

        <div className="launch-block">
          <div className="block-head">
            <EyeTracking24Regular />
            <h4>Observers</h4>
            <StepTick done={observersDone} />
          </div>
          <LaunchSentence
            flag={
              observers.read === 'ready' && observers.withoutObserver.length > 0
                ? 'withoutObserver'
                : null
            }
          >
            {observers.read === 'ready' ? (
              <>
                <strong>{observers.created}</strong> zone
                {observers.created === 1 ? '' : 's'}/equipment with an observer,{' '}
                <strong>{observers.withoutObserver.length}</strong> without one yet.
              </>
            ) : observers.read === 'error' ? (
              <>The observers on this twin could not be read, so this cannot be summarised.</>
            ) : observers.read === 'plan-error' ? (
              <>The floor plan could not be read, so this cannot be summarised.</>
            ) : (
              <>Still reading this twin&rsquo;s observers…</>
            )}
          </LaunchSentence>
          {observers.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-without-observer"
              label={`${plural(observers.withoutObserver.length, 'zone or piece', 'zones and pieces')} of equipment without an observer`}
              groups={[{ items: observers.withoutObserver }]}
            />
          )}
        </div>

        <div className="launch-block">
          <div className="block-head">
            <CoverageGlyph />
            <h4>Measurement coverage</h4>
            <StepTick done={coverageDone} />
          </div>
          {/*
            The section's subheading, in every read state (Rob, 2026-09-28): it states what this
            block IS before any sentence in it, rather than trailing the list — informative, with ONE
            exception that holds Go live: a composite measure the twin would refuse (maria@twinlabs.ai,
            2026-10-04, `composites-page-launch-refusals` — replacing Rob's 2026-09-30 ruling that
            only a duplicate did).
          */}
          <p className="coverage-note coverage-subheading">
            Only a composite measure that would be refused stops you going live.
          </p>
          {/*
            A live region, because this settles AFTER first paint — the twin's own measures arrive
            with the configuration read — so the sentence changes under a reader who has already
            moved on. Politely announced, like the Observers stage's own cross-floor count.

            ⚠ `aria-live` + `aria-atomic` rather than `role="status"`, deliberately. The two are
            equivalent for announcement (the role implies exactly this pair), but the notice Banner
            below already claims the `status` ROLE for a transient operation result, and a second
            element holding it makes "is the partial-success notice showing?" unanswerable by role
            — which three of this component's existing tests ask. Steady-state content and an
            operation result are different things; only one of them needs to be findable as a
            status.

            Three states and never two. The errored sentence carries NO number, because the plan is
            missing the twin's live measures on that path and a count taken from it would report
            entities as untracked on the strength of a read that failed. "There is nothing to
            report" and "we could not find out" are different claims.

            Each region in this block is a `<div>`, since a flagged sentence is a `Note`.
          */}
          <div aria-live="polite" aria-atomic="true">
            <LaunchSentence
              flag={coverage.read === 'ready' && coverage.names.length > 0 ? 'untracked' : null}
            >
              {coverage.read === 'ready' ? (
                coverage.names.length === 0 ? (
                  <>
                    Every zone and piece of equipment on the plan is tracked —{' '}
                    <strong>{coverage.observed}</strong> by an observer,{' '}
                    <strong>{coverage.totalled}</strong> by a composite measure.
                  </>
                ) : (
                  <>
                    <strong>{coverage.names.length}</strong> of <strong>{coverage.total}</strong>{' '}
                    zone
                    {coverage.total === 1 ? '' : 's'} and equipment{' '}
                    {coverage.names.length === 1 ? 'is' : 'are'} tracked by neither an observer nor
                    a composite measure.
                  </>
                )
              ) : coverage.read === 'error' ? (
                <>
                  What this twin already measures could not be read, so coverage cannot be
                  summarised.
                </>
              ) : coverage.read === 'no-plan' ? (
                <>No floor plan yet, so there is nothing to measure.</>
              ) : (
                <>Still reading what this twin already measures…</>
              )}
            </LaunchSentence>
          </div>
          {/* Outside the live region: an open list inside it would be re-read whole on every settle. */}
          {coverage.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-untracked"
              label={`${plural(coverage.names.length, 'untracked zone or piece', 'untracked zones and pieces')} of equipment`}
              groups={[{ items: coverage.names.map(entityItem) }]}
            />
          )}

          {/*
           * The composites the twin would refuse, and the ones that add up nothing
           * (`composites-page-launch-refusals`, R6). Always mounted with the condition inside, for
           * the reason given at the unfinished paragraph below: a region that appears at the moment
           * it has something to say is announced to nobody.
           *
           * ⛔ **The refused clause is NOT gated on the read**, and that is not an oversight. It is
           * a statement about the plan Go Live would file right now, which is complete in every
           * read state; the empty clause is a count that waits for the twin's own measures to be on
           * the plan, exactly as the unfinished one does.
           *
           * ⚠ **A failed check has its own sentence, never the empty one.** "Nothing would be
           * refused" on a check that did not run is the false claim; this says it could not check,
           * and that going live is therefore not held (R6a).
           */}
          {/*
           * The refused flag follows `held`, not the read: its own check has answered whenever it
           * holds (a check that threw reports no refusals), so it never stands on an unanswered read.
           */}
          <div aria-live="polite" aria-atomic="true">
            {checkFailed || showReleased ? (
              <p>
                {checkFailed ? (
                  <span id="launch-refusal-check-failed">
                    The composite measures could not be checked for refusals, so going live is not
                    held. The twin may still refuse one when you go live.{' '}
                  </span>
                ) : null}
                {showReleased ? (
                  <span id="launch-refused-released">
                    No composite measure would be refused now — going live is no longer held.{' '}
                  </span>
                ) : null}
              </p>
            ) : null}
            {held ? (
              <LaunchFlagNote flag="refusedComposites" id="launch-refused-composites">
                <strong>{refusedCount}</strong> composite measure{refusedCount === 1 ? '' : 's'}{' '}
                would be refused when the twin goes live. Going live is held until{' '}
                {refusedCount === 1 ? 'it is' : 'they are'} fixed in the{' '}
                <strong>Composite measures</strong> step.
              </LaunchFlagNote>
            ) : null}
            {emptyShown.length > 0 ? (
              <LaunchFlagNote flag="emptyComposites" id="launch-empty-composites">
                <strong>{emptyShown.length}</strong> composite{' '}
                {emptyShown.length === 1 ? 'measure adds' : 'measures add'} up nothing yet.{' '}
                <span className="coverage-note">
                  {emptyShown.length === 1 ? 'It' : 'They'} will not stop you going live — an empty
                  total is saved as a draft and produces no number.
                </span>
              </LaunchFlagNote>
            ) : null}
          </div>
          {/* Absent when both groups are empty, which is every no-plan case: LaunchDetail renders nothing. */}
          <LaunchDetail
            id="launch-detail-composite-refusals"
            label={describeCompositeRefusals(refusedCount, emptyShown.length)}
            groups={[
              {
                heading: 'Would be refused — stops you going live',
                // The step's own sentences, verbatim — the same words its list shows (R6).
                items: coverage.refused.map(r => ({
                  id: r.refid,
                  name: r.name,
                  detail: r.reasons.join(' '),
                })),
              },
              {
                heading: 'Empty — does not stop you going live',
                items: emptyShown.map(entityItem),
              },
            ]}
          />

          {/*
           * ⛔ **A SEPARATE SENTENCE, AND IT NEVER STOPS A LAUNCH.** A total that is not finished is
           * a different thing from a zone nothing watches: the zone is unmeasured, the total is
           * measuring less than it names. Merging the two counts would say one number about two
           * facts.
           *
           * ⚠ **Only in the `ready` state.** On an errored read the plan is missing the twin's live
           * measures, so a count taken from it would report finished totals as unfinished on the
           * strength of a read that failed — the same calibration the sentence above already makes,
           * and for the same reason.
           *
           * The names are no longer in the sentence: they are in the closed list below it
           * (`launch-summary-detail`, R5), one `<li>` each, so a comma inside a gym's name ("Bikes,
           * row 3") can no longer be mistaken for a separator.
           */}
          {/*
           * ⛔ **ALWAYS MOUNTED, WITH THE CONDITION INSIDE IT.** A screen reader announces a change
           * inside a region that ALREADY EXISTS; a region that appears at the moment it has
           * something to say is announced to nobody. This paragraph was conditionally mounted, so
           * the transition from "Still reading…" to "N composite measures are not finished" — the
           * whole point of it — was visible on screen only. The paragraph above it and both of the
           * editor's regions already follow this shape, and this change's own comments state the
           * rule it was breaking.
           */}
          <div aria-live="polite" aria-atomic="true">
            {coverage.read === 'ready' && coverage.unfinishedNames.length > 0 ? (
              <LaunchFlagNote flag="unfinishedComposites">
                <strong>{coverage.unfinishedNames.length}</strong> composite measure
                {coverage.unfinishedNames.length === 1 ? '' : 's'}{' '}
                {coverage.unfinishedNames.length === 1 ? 'is' : 'are'}{' '}
                {/*
                 * ⛔ **THE SENTENCE NAMED ONE CAUSE, and it is not the only one any more.** It read
                 * "has no camera and no total of its own" — true of a directly unfinished total,
                 * and FALSE of a parent, which is short because its CHILD is unfinished or because
                 * that child's total is excluded from processing. Naming the no-camera cause for
                 * those hands the operator a diagnosis they will act on and be wrong. The measures
                 * stage names the specific reason per measure; this count is the general statement.
                 * Raised by automated review on PR #1744.
                 */}
                not finished.{' '}
                <span className="coverage-note">
                  {partlyMeasuredLaunchNote(coverage.unfinishedNames.length)}
                </span>
              </LaunchFlagNote>
            ) : null}
          </div>
          {coverage.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-unfinished"
              label={plural(coverage.unfinishedNames.length, 'unfinished composite measure')}
              groups={[{ items: coverage.unfinishedNames.map(entityItem) }]}
            />
          )}

          {/*
           * Not finished with NOTHING measured — the one kind of total still held back from the
           * live system (`onboarding-polish-partial-composites`; Rob's wording, 2026-10-07). A
           * separate sentence from the one above, which says these totals go live: one line each,
           * so neither is false of what it counts. Same shape as above: always mounted, `ready` only.
           */}
          <div aria-live="polite" aria-atomic="true">
            {coverage.read === 'ready' && nothingMeasuredNames.length > 0 ? (
              <LaunchFlagNote flag="nothingMeasured" id="launch-nothing-measured-composites">
                <strong>{nothingMeasuredNames.length}</strong> composite measure
                {nothingMeasuredNames.length === 1 ? ' has' : 's have'} nothing measured yet.{' '}
                <span className="coverage-note">
                  {nothingMeasuredNote(nothingMeasuredNames.length)}
                </span>
              </LaunchFlagNote>
            ) : null}
          </div>
          {coverage.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-nothing-measured"
              label={plural(
                nothingMeasuredNames.length,
                'composite measure with nothing measured',
                'composite measures with nothing measured',
              )}
              groups={[{ items: nothingMeasuredNames.map(entityItem) }]}
            />
          )}

          {/*
           * Generated parent-space totals worth a person's look (`complex-measures-in-configuration-
           * parent-space-composites`, FR11a; Rob, 2026-10-01): each adds up child SPACES only, and
           * this one's space also holds something it leaves out. A SEPARATE sentence — it is neither
           * a refusal nor unfinished — and, like the unfinished one above, always mounted with the
           * condition inside it so the change is announced, and only on a `ready` read.
           */}
          <div aria-live="polite" aria-atomic="true">
            {coverage.read === 'ready' && reviewShown.length > 0 ? (
              <LaunchFlagNote flag="generatedReview">
                <strong>{reviewShown.length}</strong> generated total
                {reviewShown.length === 1 ? ' needs' : 's need'} review.{' '}
                <span className="coverage-note">
                  {reviewShown.length === 1 ? 'It adds' : 'They add'} up child spaces only and leave
                  {reviewShown.length === 1 ? 's' : ''} something out — equipment standing in the
                  space, or a floor with no usable name. This will not stop you going live.
                </span>
              </LaunchFlagNote>
            ) : null}
          </div>
          {coverage.read === 'ready' && (
            <LaunchDetail
              id="launch-detail-review"
              label={`${plural(reviewShown.length, 'generated total')} to review`}
              groups={[
                {
                  heading: 'Needs review — does not stop you going live',
                  items: reviewShown.map(entityItem),
                },
              ]}
            />
          )}
        </div>

        {summary.biGaps && (
          <BiHierarchiesBlock
            biGaps={summary.biGaps}
            rechecking={biRechecking}
            onRecheck={onRecheckBi}
          />
        )}
      </div>

      <div className="go-live">
        <Button
          variant="primary"
          size="full"
          type="button"
          ref={goLiveRef}
          onClick={onGoLive}
          disabled={activating || held || biHolding || lockHeld || hoursHeld}
          aria-describedby={
            [
              held ? 'launch-go-live-held' : null,
              lockHeld ? 'launch-go-live-locked' : null,
              hoursHeld ? 'launch-go-live-hours' : null,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
        >
          {activating ? 'Activating…' : goLiveLabel}
        </Button>
        {/*
         * Why the button is held, where the button is — the reason itself is up in Measurement
         * coverage, a long scroll away on a large gym. In the stops-go-live role, like the hours
         * line beside it.
         */}
        {held && (
          <LaunchFlagNote flag="refusedComposites" id="launch-go-live-held">
            {goLiveLabel} is held: <strong>{refusedCount}</strong> composite measure
            {refusedCount === 1 ? '' : 's'} would be refused. See Measurement coverage above.
          </LaunchFlagNote>
        )}
        {hoursLine?.kind === 'checking' && (
          <p id="launch-go-live-hours" className="go-live-held">
            Checking this twin&rsquo;s opening hours&hellip;
          </p>
        )}
        {hoursLine?.kind === 'failed' && (
          /* The step's one treatment for a read that failed and can be retried, as the lock's below. */
          <Banner surface="on-glass" tone="error">
            <span id="launch-go-live-hours">
              Couldn&rsquo;t check this twin&rsquo;s opening hours.
            </span>
            {onRecheckHours && (
              <div className="retry-slot">
                {/* `loading`, not `disabled`: the button keeps focus and says it is busy. The
                    name starts with what it shows, so a spoken "Check again" finds it. */}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onRecheckHours}
                  loading={hoursRechecking}
                  aria-label="Check again: this twin’s opening hours"
                >
                  {hoursRechecking ? 'Checking…' : 'Check again'}
                </Button>
              </div>
            )}
          </Banner>
        )}
        {hoursLine?.kind === 'note' && (
          <Note tone="error" bare id="launch-go-live-hours" icon={<Icon name="rocket" size="xs" />}>
            {hoursLine.text}
          </Note>
        )}
        {biFailure && (
          <LaunchBiNotice
            failure={biFailure}
            failures={bi?.failures ?? 1}
            running={bi?.status === 'running'}
            saving={biOutcomeSaving}
            onRetry={() => onRetryBi?.()}
            onContinue={() => onContinueWithoutBi?.()}
          />
        )}
        {lock?.state === 'checking' && !recheckingAfterUnknown && (
          <p id="launch-go-live-locked" className="go-live-held">
            Checking when this twin was last launched…
          </p>
        )}
        {lock?.state === 'locked' && lock.endsAt !== null && (
          <LockCountdown
            endsAt={lock.endsAt}
            launchedAt={lock.launchedAt}
            label={goLiveLabel}
            inProgress={lock.inProgress}
          />
        )}
        {(lock?.state === 'unknown' || recheckingAfterUnknown) && (
          /*
           * The wizard's one treatment for a read that failed and can be retried: the error Banner
           * with a ghost, compact Check again in its action row (AssetCsvUploader, OnboardingCameras).
           * The button stays mounted, disabled, while it re-checks, so keyboard focus is not lost.
           */
          <Banner surface="on-glass" tone="error">
            <span id="launch-go-live-locked">
              Couldn&apos;t check when this twin was last launched.
            </span>
            {onRecheckLock && (
              <div className="retry-slot">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={onRecheckLock}
                  disabled={recheckingAfterUnknown}
                >
                  {recheckingAfterUnknown ? 'Checking…' : 'Check again'}
                </Button>
              </div>
            )}
          </Banner>
        )}
        {/*
         * A polite live region WITHOUT the status role, as the coverage sentence above does:
         * the notice Banner owns that role on this step.
         */}
        <p
          className="go-live-lock-announcement"
          aria-live="polite"
          aria-atomic="true"
          style={visuallyHidden}
        >
          {lockAnnouncement}
        </p>
        {/* The hours' own polite region, announced on transitions only, beside the lock's. */}
        <p
          className="go-live-hours-announcement"
          aria-live="polite"
          aria-atomic="true"
          style={visuallyHidden}
        >
          {hoursAnnouncement}
        </p>
        {/*
         * The design system's notices: the error Banner is an `alert`, the info Banner a `status`.
         * `go-live-error` is unstyled: the agent-run capture rigs find a Go live failure by it.
         */}
        {error && (
          <Banner surface="on-glass" tone="error" className="go-live-error">
            {error}
          </Banner>
        )}
        {!error && notice && (
          <Banner surface="on-glass" tone="info">
            {notice}
            <div className="retry-slot">
              <Button variant="frosted" onClick={onContinue}>
                Go to twin
              </Button>
            </div>
          </Banner>
        )}
      </div>
    </Card>
  );
};

export default styled(OnboardingLaunch)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 640px;
  margin: 0 auto;

  .intro {
    margin: 0;
    font-size: 0.9375rem;
    color: ${semantic.text.secondary};
    line-height: 1.5;
  }

  .blocks {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  /* The step list's tick colour (OnboardingNav), so both ticks read one success colour. */
  .tick {
    display: inline-flex;
    margin-left: auto;
    color: ${semantic.text.success};
  }

  .block-head {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 0.4rem;
    color: ${brand.cyan};
  }

  .block-head h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${semantic.text.primary};
  }

  .launch-block p {
    margin: 0;
    font-size: 0.875rem;
    color: ${semantic.text.secondary};
    line-height: 1.5;
  }

  .launch-block strong {
    color: ${semantic.text.primary};
  }

  /* A flag's bold count takes its note's role ink, not the body's primary ink. */
  .tl-note strong {
    color: inherit;
  }

  /* Inherits .block-head's colour through currentColor, so it introduces no literal of its own. */
  .coverage-glyph {
    flex-shrink: 0;
  }

  /* Tokenised: the R5 design-system alignment covers the literals already in this file, it does not
     license adding more. */
  .coverage-note {
    color: ${ink[4]};
  }

  /* The section's subheading sits between its heading and its first sentence. */
  .launch-block p.coverage-subheading {
    margin-bottom: ${space[1]};
    color: ${ink[4]};
  }

  .go-live {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    align-self: center;
  }

  /* Keep the button its original width; let the error message be readable. */
  .go-live > *:first-child {
    width: 200px;
  }

  /* Tokenised: the R5 design-system alignment covers the literals already here, not new ones. */
  .go-live-held {
    margin: 0;
    max-width: 420px;
    text-align: center;
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${ink[4]};
  }

  .go-live .retry-slot {
    margin-top: ${space[2]};
  }
`;
